import React, { useState } from 'react';
import { PublicTemplate } from '../../types/publicTypes';
import { useNavigation } from '../../context/NavigationContext';
import { PlatformAttribution } from '../../../components/common/PlatformAttribution';
import {
  X,
  Monitor,
  Tablet,
  Smartphone,
  CheckCircle2,
  ArrowRight,
  ExternalLink,
  Layers,
  Sparkles,
} from 'lucide-react';

interface TemplatePreviewModalProps {
  template: PublicTemplate | null;
  onClose: () => void;
}

export const TemplatePreviewModal: React.FC<TemplatePreviewModalProps> = ({
  template,
  onClose,
}) => {
  const { navigate } = useNavigation();
  const [device, setDevice] = useState<'desktop' | 'tablet' | 'mobile'>('desktop');

  if (!template) return null;

  const handleStart = () => {
    onClose();
    navigate('/kayit');
  };

  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-slate-950/95 backdrop-blur-md animate-in fade-in duration-200">
      {/* Top Control Bar */}
      <div className="h-16 px-4 sm:px-6 bg-slate-900 border-b border-slate-800 flex items-center justify-between gap-4">
        {/* Template Info */}
        <div className="flex items-center gap-3">
          <button
            onClick={onClose}
            className="p-2 rounded-xl text-slate-400 hover:text-white hover:bg-slate-800 transition"
            title="Kapat"
          >
            <X className="w-5 h-5" />
          </button>
          <div>
            <div className="flex items-center gap-2">
              <h3 className="text-white font-bold text-sm sm:text-base">
                {template.name}
              </h3>
              <span className="text-[10px] px-2 py-0.5 rounded bg-blue-500/10 text-blue-400 border border-blue-500/20 font-semibold">
                {template.categoryName}
              </span>
            </div>
            <p className="text-slate-400 text-xs hidden sm:block">
              {template.description}
            </p>
          </div>
        </div>

        {/* Device Mode Switcher */}
        <div className="flex items-center gap-1 bg-slate-950 p-1 rounded-xl border border-slate-800">
          <button
            onClick={() => setDevice('desktop')}
            className={`p-2 rounded-lg text-xs font-medium flex items-center gap-1.5 transition ${
              device === 'desktop'
                ? 'bg-blue-600 text-white shadow-sm'
                : 'text-slate-400 hover:text-white hover:bg-slate-800'
            }`}
            title="Masaüstü Görünümü"
          >
            <Monitor className="w-4 h-4" />
            <span className="hidden md:inline">Masaüstü</span>
          </button>
          <button
            onClick={() => setDevice('tablet')}
            className={`p-2 rounded-lg text-xs font-medium flex items-center gap-1.5 transition ${
              device === 'tablet'
                ? 'bg-blue-600 text-white shadow-sm'
                : 'text-slate-400 hover:text-white hover:bg-slate-800'
            }`}
            title="Tablet Görünümü"
          >
            <Tablet className="w-4 h-4" />
            <span className="hidden md:inline">Tablet</span>
          </button>
          <button
            onClick={() => setDevice('mobile')}
            className={`p-2 rounded-lg text-xs font-medium flex items-center gap-1.5 transition ${
              device === 'mobile'
                ? 'bg-blue-600 text-white shadow-sm'
                : 'text-slate-400 hover:text-white hover:bg-slate-800'
            }`}
            title="Mobil Görünümü"
          >
            <Smartphone className="w-4 h-4" />
            <span className="hidden md:inline">Mobil</span>
          </button>
        </div>

        {/* Primary Action Button */}
        <div className="flex items-center gap-3">
          <button
            onClick={handleStart}
            className="px-5 py-2.5 rounded-xl bg-blue-600 hover:bg-blue-500 text-white text-xs sm:text-sm font-semibold shadow-lg shadow-blue-600/30 flex items-center gap-2 transition active:scale-95 whitespace-nowrap"
          >
            <span>Bu Şablonla Başla</span>
            <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Main Preview Stage */}
      <div className="flex-1 overflow-y-auto p-4 sm:p-8 flex justify-center items-start bg-slate-950/80">
        <div
          className={`transition-all duration-300 bg-slate-900 border border-slate-800 rounded-2xl shadow-2xl overflow-hidden flex flex-col ${
            device === 'desktop'
              ? 'w-full max-w-6xl'
              : device === 'tablet'
              ? 'w-[768px]'
              : 'w-[390px]'
          }`}
        >
          {/* Simulated Browser Bar */}
          <div className="h-9 px-4 bg-slate-900 border-b border-slate-800 flex items-center justify-between text-xs text-slate-400 select-none">
            <div className="flex items-center gap-1.5">
              <span className="w-2.5 h-2.5 rounded-full bg-rose-500/80" />
              <span className="w-2.5 h-2.5 rounded-full bg-amber-500/80" />
              <span className="w-2.5 h-2.5 rounded-full bg-emerald-500/80" />
            </div>
            <div className="px-4 py-0.5 rounded-md bg-slate-950 border border-slate-800 text-[11px] text-slate-300 font-mono flex items-center gap-2">
              <span className="text-emerald-400">https://</span>
              <span>adiosoft.com/{template.slug}</span>
            </div>
            <div className="w-10" />
          </div>

          {/* Simulated Live Template View */}
          <div className="bg-slate-950 text-slate-100 min-h-[600px]">
            {/* Header Simulator */}
            <div className="p-6 border-b border-slate-800/80 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div
                  className="w-8 h-8 rounded-lg flex items-center justify-center font-bold text-white shadow-md"
                  style={{ backgroundColor: template.colors.accent }}
                >
                  {template.name.charAt(0)}
                </div>
                <span className="font-bold text-base tracking-tight text-white">
                  {template.name}
                </span>
              </div>
              <div className="hidden sm:flex items-center gap-4 text-xs text-slate-300">
                {template.pages.map((p, i) => (
                  <span key={i} className="hover:text-white cursor-pointer">
                    {p}
                  </span>
                ))}
              </div>
              <button
                style={{ backgroundColor: template.colors.accent }}
                className="px-4 py-1.5 rounded-lg text-white text-xs font-semibold shadow-sm"
              >
                İletişim / Teklif
              </button>
            </div>

            {/* Hero Section Simulator */}
            <div className="relative p-8 sm:p-14 overflow-hidden border-b border-slate-800/60">
              <div className="relative z-10 max-w-2xl">
                <span
                  className="text-xs font-semibold px-2.5 py-1 rounded-full uppercase tracking-wider mb-3 inline-block"
                  style={{
                    backgroundColor: `${template.colors.accent}20`,
                    color: template.colors.accent,
                    borderColor: `${template.colors.accent}40`,
                  }}
                >
                  {template.categoryName}
                </span>
                <h1 className="text-2xl sm:text-4xl font-extrabold text-white leading-tight mb-4">
                  {template.category === 'YACHT_RENTAL'
                    ? 'Ege & Akdeniz Koylarında Kusursuz VIP Mavi Yolculuk'
                    : template.category === 'LEGAL'
                    ? 'Hukuki Danışmanlık ve Stratejik Arabuluculuk Hizmetleri'
                    : template.category === 'HEALTH'
                    ? 'Modern Sağlık & Gülüş Tasarımı ile Hayata Yeniden Gülümseyin'
                    : `${template.name} ile Kurumsal Güç ve İnovasyon`}
                </h1>
                <p className="text-slate-300 text-sm leading-relaxed mb-6">
                  {template.description}
                </p>
                <div className="flex flex-wrap gap-3">
                  <button
                    style={{ backgroundColor: template.colors.accent }}
                    className="px-5 py-2.5 rounded-xl text-white text-xs font-semibold shadow-lg"
                  >
                    {template.category === 'YACHT_RENTAL'
                      ? 'Filoyu İncele & Rezervasyon Yap'
                      : 'Hemen Teklif Alın'}
                  </button>
                  <button className="px-5 py-2.5 rounded-xl bg-slate-900 border border-slate-700 text-white text-xs font-semibold">
                    Hakkımızda Detaylı Bilgi
                  </button>
                </div>
              </div>

              {/* Background Cover Image Banner */}
              <div className="mt-8 rounded-2xl overflow-hidden border border-slate-800 shadow-xl aspect-[16/8]">
                <img
                  src={template.fullPreviewImage}
                  alt={template.name}
                  className="w-full h-full object-cover"
                />
              </div>
            </div>

            {/* Template Features Breakdown */}
            <div className="p-8 sm:p-12 bg-slate-900/40">
              <div className="text-center max-w-xl mx-auto mb-8">
                <h4 className="text-lg font-bold text-white mb-2">
                  Bu Şablonda Yer Alan Dahili Modüller
                </h4>
                <p className="text-xs text-slate-400">
                  Hiçbir ekstra eklenti veya kod gerekmeden kullanıma hazırdır.
                </p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                {template.features.map((feat, idx) => (
                  <div
                    key={idx}
                    className="p-4 rounded-xl bg-slate-900/90 border border-slate-800 flex items-start gap-3"
                  >
                    <div
                      className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0"
                      style={{ backgroundColor: `${template.colors.accent}20` }}
                    >
                      <CheckCircle2
                        className="w-4 h-4"
                        style={{ color: template.colors.accent }}
                      />
                    </div>
                    <div>
                      <h5 className="text-white text-xs font-semibold mb-0.5">
                        {feat}
                      </h5>
                      <span className="text-[11px] text-slate-400">
                        Özelleştirilebilir ve anında yayına hazır.
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Mandatory Platform Attribution */}
            <PlatformAttribution isDark={true} />
          </div>
        </div>
      </div>
    </div>
  );
};
